/**
 * Composite Analyzer: ATM Bracket Calibration (atm scope)
 *
 * The existing atmBarContext.detectStopTooTight asks "did losers go almost
 * to your stop?" — a behavioral question. This analyzer asks the
 * STRUCTURAL one: "does your stop, in absolute points, match the distribution
 * of adverse moves you actually see?" Same for targets vs MFE.
 *
 * It's the bracket-as-instrument vs market-reality check. Three patterns:
 *
 *   1. Stop too tight vs MAE distribution. If the bracket's stopLossTicks
 *      lands at <30th percentile of typical maePoints, most adverse moves
 *      exceed the stop — you're stopping out at noise. Different from
 *      detectStopTooTight which only looks at losers; this looks at the
 *      full distribution including winners that survived adverse moves.
 *
 *   2. Stop too loose vs MAE distribution. Stop above the 75th percentile
 *      means losers rarely come close to it — risk is wider than needed,
 *      drawing oversized R-loss when the trade does fail.
 *
 *   3. Target undersized vs MFE distribution. If the first profit target
 *      (the smallest targetTicks > 0) lands well below median winner MFE,
 *      the bracket is capping winners before their typical peak. Pairs with
 *      the existing leakage analyzer but is prescriptive (hard-numbers
 *      target recommendation, not just "your capture is low").
 *
 * Reads:
 *   ntTemplateVersions[atmName][0].snapshot.brackets[]   — actual bracket params
 *   trade.barAnalysis.maePoints / mfePoints              — adverse / favorable in points
 *   trade.stopLossPoints                                 — used to derive tickSize
 *
 * Trades without barAnalysis are silently skipped. Trades with no stop and
 * no version info are also skipped — the analyzer needs at least one trade
 * to anchor the tickSize conversion.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';

export const key = 'atmBracketCalibration';
export const tier = 'free';

// Sample-size guard. Need enough adverse/favorable observations to compute
// percentiles meaningfully — fewer than this and the percentiles are noise.
const MIN_TRADES_WITH_BARS = 6;
// Distribution thresholds. "Too tight" / "too loose" / "undersized" are
// chosen to be conservative so we don't fire on borderline calibration.
const STOP_TIGHT_PERCENTILE_MAX  = 0.30;  // Stop sits below 30th percentile of typical MAE = TIGHT
const STOP_LOOSE_PERCENTILE_MIN  = 0.85;  // Stop sits above 85th percentile of typical MAE = LOOSE
const TARGET_UNDERSIZED_PERCENTILE_MAX = 0.40;  // First target below 40th percentile of winner MFE = UNDERSIZED

const fmtPts = (n) => Number.isFinite(n) ? n.toFixed(1) : '—';

/**
 * Compute the percentile rank (0..1) of a value within a sorted ascending
 * array. If the value falls between two elements, interpolate linearly.
 * Returns null when the array is empty.
 */
export const percentileOf = (sortedAsc, value) => {
  if (!Array.isArray(sortedAsc) || sortedAsc.length === 0) return null;
  if (!Number.isFinite(value)) return null;
  if (value <= sortedAsc[0]) return 0;
  if (value >= sortedAsc[sortedAsc.length - 1]) return 1;
  // Binary search for the insertion point.
  let lo = 0, hi = sortedAsc.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sortedAsc[mid] < value) lo = mid + 1; else hi = mid;
  }
  // Linear interpolation between lo-1 and lo.
  const left = sortedAsc[lo - 1];
  const right = sortedAsc[lo];
  if (right === left) return lo / (sortedAsc.length - 1);
  const fracInBin = (value - left) / (right - left);
  return (lo - 1 + fracInBin) / (sortedAsc.length - 1);
};

/**
 * Compute median of a sorted-ascending array. Returns null when empty.
 */
export const median = (sortedAsc) => {
  if (!Array.isArray(sortedAsc) || sortedAsc.length === 0) return null;
  const n = sortedAsc.length;
  const mid = n >> 1;
  return n % 2 === 0 ? (sortedAsc[mid - 1] + sortedAsc[mid]) / 2 : sortedAsc[mid];
};

/**
 * Derive tickSize (price units per tick) from any trade with both
 * stopLossPoints (price) and a known stopLossTicks (from the bracket
 * snapshot). Returns null if neither field is present on any trade.
 *
 * stopLossPoints is captured at trade-entry time — it's derived from the
 * bracket the trader was actually using, so the math is consistent.
 */
const deriveTickSize = (atmTrades, stopLossTicks) => {
  if (!Number.isFinite(stopLossTicks) || stopLossTicks <= 0) return null;
  for (const t of atmTrades) {
    const slPts = parseFloat(t?.stopLossPoints);
    if (Number.isFinite(slPts) && slPts > 0) {
      const ts = slPts / stopLossTicks;
      // Sanity bounds — futures tick sizes range 0.01 (micro) to 1.0 (some
      // bonds). Anything outside this is a sign of mismatched bracket.
      if (ts >= 0.005 && ts <= 5) return ts;
    }
  }
  return null;
};

const getCurrentBrackets = (ntTemplateVersions, atmName) => {
  const versions = ntTemplateVersions?.[atmName];
  if (!Array.isArray(versions) || versions.length === 0) return null;
  const snap = versions[0]?.snapshot;
  return Array.isArray(snap?.brackets) ? snap.brackets : null;
};

// ── Pattern 1 + 2: Stop calibration vs MAE distribution ──────────────────

const detectStopCalibration = (atmName, atmTrades, brackets) => {
  if (!brackets || brackets.length === 0) return null;
  // Use the FIRST bracket's stop as the universal stop — futures ATMs almost
  // always share a single hard stop across brackets even when targets differ.
  const stopTicks = brackets[0]?.stopLossTicks;
  if (!Number.isFinite(stopTicks) || stopTicks <= 0) return null;
  const tickSize = deriveTickSize(atmTrades, stopTicks);
  if (!tickSize) return null;
  const stopPts = stopTicks * tickSize;

  // MAE distribution across ALL trades (winners + losers) with maePoints.
  const maeArr = atmTrades
    .map(t => parseFloat(t?.barAnalysis?.maePoints))
    .filter(n => Number.isFinite(n) && n > 0)
    .sort((a, b) => a - b);
  if (maeArr.length < MIN_TRADES_WITH_BARS) return null;

  const stopPctile = percentileOf(maeArr, stopPts);
  if (stopPctile == null) return null;
  const med = median(maeArr);
  const p75 = maeArr[Math.floor(maeArr.length * 0.75)];

  // TIGHT
  if (stopPctile <= STOP_TIGHT_PERCENTILE_MAX) {
    return makeObservation({
      id: `${key}:${atmName}:stop-tight-vs-mae`,
      analyzer: key,
      scope: 'atm',
      scopeId: atmName,
      severity: SEVERITY.WARNING,
      category: CATEGORY.EXECUTION,
      title: `"${atmName}" stop ${fmtPts(stopPts)}pts is at the ${Math.round(stopPctile * 100)}th percentile of your typical adverse moves`,
      detail: `Across ${maeArr.length} trades on this ATM, median MAE was ${fmtPts(med)}pts; 75th percentile was ${fmtPts(p75)}pts. Your ${fmtPts(stopPts)}pt stop sits below most of the noise the market routinely produces — losers aren't failed entries, they're stopped early.`,
      action: `Consider widening the stop toward the 50th-60th percentile (~${fmtPts(med)}pts) and reducing position size to keep R the same. Risk stays constant; survival rate improves.`,
      evidence: {
        stopTicks, stopPts, tickSize,
        maeMedian: med, maeP75: p75,
        stopPercentile: stopPctile,
        tradeCount: maeArr.length,
      },
      confidence: 0.78,
      priority: 82,
    });
  }
  // LOOSE
  if (stopPctile >= STOP_LOOSE_PERCENTILE_MIN) {
    return makeObservation({
      id: `${key}:${atmName}:stop-loose-vs-mae`,
      analyzer: key,
      scope: 'atm',
      scopeId: atmName,
      severity: SEVERITY.INFO,
      category: CATEGORY.RISK,
      title: `"${atmName}" stop ${fmtPts(stopPts)}pts is at the ${Math.round(stopPctile * 100)}th percentile of typical adverse moves`,
      detail: `Median MAE was ${fmtPts(med)}pts; 75th percentile was ${fmtPts(p75)}pts. Your ${fmtPts(stopPts)}pt stop is wider than ${Math.round(stopPctile * 100)}% of the adverse moves you actually experience — risk is bigger than the trade actually needs.`,
      action: `Consider tightening toward the 70th-80th percentile (~${fmtPts(p75)}pts) and reinvesting the saved risk in size. Same R loss, more contracts.`,
      evidence: {
        stopTicks, stopPts, tickSize,
        maeMedian: med, maeP75: p75,
        stopPercentile: stopPctile,
        tradeCount: maeArr.length,
      },
      confidence: 0.72,
      priority: 70,
    });
  }
  return null;
};

// ── Pattern 3: First-target undersized vs winner MFE ────────────────────

const detectTargetCalibration = (atmName, atmTrades, brackets) => {
  if (!brackets || brackets.length === 0) return null;
  // Find the smallest non-zero targetTicks across brackets — that's the
  // first target the trader is firing at.
  let firstTargetTicks = null;
  for (const b of brackets) {
    const t = b?.targetTicks;
    if (Number.isFinite(t) && t > 0 && (firstTargetTicks == null || t < firstTargetTicks)) {
      firstTargetTicks = t;
    }
  }
  if (!firstTargetTicks) return null;

  // Need a stopTicks reference to derive tickSize.
  const stopTicks = brackets[0]?.stopLossTicks;
  const tickSize = deriveTickSize(atmTrades, stopTicks);
  if (!tickSize) return null;
  const targetPts = firstTargetTicks * tickSize;

  // MFE distribution across WINNERS only — losers' MFE is usually small and
  // would skew the percentile calculation against actually-hit targets.
  const winnerMfe = atmTrades
    .filter(t => Number.isFinite(t?.netPnl) && t.netPnl > 0)
    .map(t => parseFloat(t?.barAnalysis?.mfePoints))
    .filter(n => Number.isFinite(n) && n > 0)
    .sort((a, b) => a - b);
  if (winnerMfe.length < MIN_TRADES_WITH_BARS) return null;

  const targetPctile = percentileOf(winnerMfe, targetPts);
  if (targetPctile == null) return null;
  if (targetPctile > TARGET_UNDERSIZED_PERCENTILE_MAX) return null;

  const med = median(winnerMfe);
  const p75 = winnerMfe[Math.floor(winnerMfe.length * 0.75)];

  return makeObservation({
    id: `${key}:${atmName}:target-undersized`,
    analyzer: key,
    scope: 'atm',
    scopeId: atmName,
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    title: `"${atmName}" first target ${fmtPts(targetPts)}pts caps below typical winner MFE`,
    detail: `${winnerMfe.length} winners on this ATM had median MFE ${fmtPts(med)}pts and 75th percentile ${fmtPts(p75)}pts. Your first target at ${fmtPts(targetPts)}pts sits at the ${Math.round(targetPctile * 100)}th percentile — most winners run past it before the bracket exits.`,
    action: `Push the first target to ~${fmtPts(med)}pts (median MFE) or convert one bracket into a runner with a structural trail. The market is delivering more than the bracket is asking for.`,
    evidence: {
      firstTargetTicks, targetPts, tickSize,
      mfeMedian: med, mfeP75: p75,
      targetPercentile: targetPctile,
      winnerCount: winnerMfe.length,
    },
    confidence: 0.78,
    priority: 80,
  });
};

// ── Public analyze() ─────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { atmName, atmTrades, ntTemplateVersions } = ctx || {};
  if (!atmName || !Array.isArray(atmTrades) || atmTrades.length === 0) return null;

  const brackets = getCurrentBrackets(ntTemplateVersions, atmName);
  if (!brackets) return null;

  // Live-trade gate: target calibration filters trades by `netPnl > 0` to
  // build winner-MFE percentiles. Open trades have unrealized P&L which
  // isn't a realized outcome — counting an open trade as a "winner" because
  // it's momentarily green would skew the MFE distribution. Filter once at
  // the top so MAE distribution (stop calibration) also runs on closed
  // trades only — same closed-only semantics as summarizePnl.
  const closedAtmTrades = atmTrades.filter(t => t?.isOpen !== true);
  if (closedAtmTrades.length === 0) return null;

  const out = [];
  const stopObs = detectStopCalibration(atmName, closedAtmTrades, brackets);
  if (stopObs) out.push(stopObs);
  const targetObs = detectTargetCalibration(atmName, closedAtmTrades, brackets);
  if (targetObs) out.push(targetObs);
  return out.length === 0 ? null : out;
};

// Exports for tests
export {
  deriveTickSize,
  getCurrentBrackets,
  detectStopCalibration,
  detectTargetCalibration,
  MIN_TRADES_WITH_BARS,
  STOP_TIGHT_PERCENTILE_MAX,
  STOP_LOOSE_PERCENTILE_MIN,
  TARGET_UNDERSIZED_PERCENTILE_MAX,
};
